import React, { useCallback, useEffect, useState } from "react";
import { usePermission } from "../../helpers/usePermission";
import {
  getChartAnnotations,
  createChartAnnotation,
  deleteChartAnnotation,
} from "../../helpers/fakebackend_helper";

export const ANNOTATION_CATEGORIES = [
  { id: "content", label: "Content drop" },
  { id: "kol",     label: "KOL post" },
  { id: "space",   label: "X Space" },
  { id: "release", label: "Store release" },
  { id: "paid",    label: "Paid campaign" },
  { id: "other",   label: "Other" },
];

/**
 * Loads the annotations for one chart. Kept separate from useCachedRequest on
 * purpose: these are written from the same screen that reads them, so a stale
 * cached copy right after an add is worse than a spinner.
 */
export function useChartAnnotations(chartKey = "wew") {
  const [annotations, setAnnotations] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    try {
      const res = await getChartAnnotations({ chart_key: chartKey });
      setAnnotations((res && res.annotations) || []);
    } catch (_) {
      // A missing/forbidden annotations endpoint must never break the chart it
      // decorates — fall back to "no markers".
      setAnnotations([]);
    } finally {
      setLoading(false);
    }
  }, [chartKey]);

  useEffect(() => { reload(); }, [reload]);

  return { annotations, loading, reload };
}

/**
 * Maps annotations onto a weekly series as fractional x positions (0..1).
 *
 * The WEW trend is Monday-anchored weekly buckets, so a Tuesday KOL drop can only
 * be pinned to its WEEK — that is a real limit of the underlying series, not of
 * this component. Anything outside the rendered window is dropped rather than
 * clamped onto the edge, which would imply an event happened when it did not.
 */
export function mapAnnotationsToSeries(annotations, series) {
  if (!series || series.length === 0) return [];
  const starts = series.map((s) => new Date(`${s.week_start}T00:00:00.000Z`).getTime());
  const WEEK = 7 * 24 * 60 * 60 * 1000;
  const last = starts.length - 1;

  return (annotations || [])
    .map((a) => {
      const t = new Date(`${a.event_date}T00:00:00.000Z`).getTime();
      if (Number.isNaN(t)) return null;
      if (t < starts[0] || t >= starts[last] + WEEK) return null;
      let idx = starts.findIndex((s, i) => t >= s && (i === last ? t < s + WEEK : t < starts[i + 1]));
      if (idx < 0) return null;
      return { x: last === 0 ? 0 : idx / last, label: a.label, category: a.category, id: a.id };
    })
    .filter(Boolean);
}

/** Add / remove events. Rendered under the chart; hidden without nsm.annotate. */
export function AnnotationManager({ chartKey = "wew", annotations, onChange }) {
  const can = usePermission();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ event_date: "", label: "", category: "content" });

  if (!can("nsm.annotate")) return null;

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.event_date || !form.label.trim()) {
      setError("Pick a date and give it a label.");
      return;
    }
    setSaving(true);
    try {
      await createChartAnnotation({ ...form, label: form.label.trim(), chart_key: chartKey });
      setForm({ event_date: "", label: "", category: "content" });
      setOpen(false);
      if (onChange) await onChange();
    } catch (err) {
      setError(typeof err === "string" ? err : err?.message || "Could not save that event");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await deleteChartAnnotation(id);
      if (onChange) await onChange();
    } catch (_) { /* list simply won't change */ }
  };

  const input = {
    padding: "6px 8px", borderRadius: 6, border: "1px solid var(--bl-line)",
    background: "var(--bl-panel)", color: "var(--bl-text-strong)", fontSize: 12,
  };

  return (
    <div style={{ marginTop: 10, borderTop: "1px solid var(--bl-line)", paddingTop: 8 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontSize: 11, color: "var(--bl-text-soft)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
          Events ({(annotations || []).length})
        </span>
        <button
          onClick={() => setOpen(!open)}
          style={{ background: "transparent", border: 0, color: "var(--bl-accent)", cursor: "pointer", fontSize: 12 }}
        >
          {open ? "Cancel" : "+ Add event"}
        </button>
      </div>

      {open && (
        <form onSubmit={submit} style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
          <input
            type="date" style={input} value={form.event_date}
            onChange={(e) => setForm({ ...form, event_date: e.target.value })}
          />
          <input
            type="text" placeholder="e.g. KOL drop — @handle" maxLength={120}
            style={{ ...input, flex: 1, minWidth: 180 }} value={form.label}
            onChange={(e) => setForm({ ...form, label: e.target.value })}
          />
          <select
            style={input} value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
          >
            {ANNOTATION_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
          <button type="submit" className="bl-btn-primary" disabled={saving} style={{ padding: "6px 14px", fontSize: 12 }}>
            {saving ? "Saving…" : "Save"}
          </button>
        </form>
      )}

      {error && <div style={{ marginTop: 6, fontSize: 11, color: "#ff5d6c" }}>{error}</div>}

      {(annotations || []).length > 0 && (
        <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 3 }}>
          {annotations.map((a) => (
            <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, color: "var(--bl-text-soft)" }}>
              <span style={{ fontFamily: "var(--bl-font-numeric)", minWidth: 74 }}>{a.event_date}</span>
              <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "var(--bl-text-strong)" }}>
                {a.label}
              </span>
              <button
                onClick={() => remove(a.id)} title="Remove"
                style={{ background: "transparent", border: 0, color: "var(--bl-text-soft)", cursor: "pointer", fontSize: 12 }}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
